/**
 * WebRTC peer link for one match (ARCHITECTURE §6): an RTCPeerConnection with W3C "perfect negotiation" over the
 * lobby's `signal` messages and ONE pre-negotiated DataChannel `{negotiated: true, id: 0, ordered: false,
 * maxRetransmits: 0}` (UDP-like, both sides create it, no ondatachannel race).
 *
 * Roles come from match_found: the impolite peer (HOME) offers first and wins glare; the polite peer rolls back
 * its own offer when an offer collides. ICE candidates trickle as they appear; candidates that arrive before the
 * remote description are queued (some browsers reject them otherwise).
 */
import type { IceServerConfig, SignalData } from '@hf/shared';
import type { NetEnv } from './env';

/** Always offered in addition to the server's list (ARCHITECTURE §6). */
export const DEFAULT_STUN = 'stun:stun.l.google.com:19302';

export const iceServersWithDefault = (servers: readonly IceServerConfig[]): RTCIceServer[] => {
  const out: RTCIceServer[] = servers.map((s) => ({
    urls: s.urls,
    ...(s.username !== undefined ? { username: s.username } : {}),
    ...(s.credential !== undefined ? { credential: s.credential } : {}),
  }));
  const hasDefault = out.some((s) => (Array.isArray(s.urls) ? s.urls : [s.urls]).includes(DEFAULT_STUN));
  if (!hasDefault) out.push({ urls: DEFAULT_STUN });
  return out;
};

export const DATA_CHANNEL_LABEL = 'hf-game';
export const DATA_CHANNEL_INIT: RTCDataChannelInit = {
  negotiated: true,
  id: 0,
  ordered: false,
  maxRetransmits: 0,
};

export interface PeerLinkOptions {
  polite: boolean;
  iceServers: readonly IceServerConfig[];
  /** Send a signaling payload to the other peer (through the lobby server). */
  signal(data: SignalData): void;
  /** The DataChannel opened (may fire again after an ICE restart re-opens a new association). */
  onOpen(dc: RTCDataChannel): void;
  /** The connection failed for good (ICE failed / closed): fall back to relay right away. */
  onFailed(reason: string): void;
}

export class PeerLink {
  readonly pc: RTCPeerConnection;
  readonly dc: RTCDataChannel;
  private makingOffer = false;
  private ignoreOffer = false;
  private settingRemoteAnswer = false;
  private closed = false;
  private readonly pendingCandidates: RTCIceCandidateInit[] = [];
  private failedReported = false;
  /** Serializes signal handling so descriptions/candidates are applied in arrival order. */
  private chain: Promise<void> = Promise.resolve();

  constructor(
    private readonly env: NetEnv,
    Ctor: new (config: RTCConfiguration) => RTCPeerConnection,
    private readonly opts: PeerLinkOptions,
  ) {
    this.pc = new Ctor({ iceServers: iceServersWithDefault(opts.iceServers) });
    const pc = this.pc;
    this.dc = pc.createDataChannel(DATA_CHANNEL_LABEL, DATA_CHANNEL_INIT);
    try {
      this.dc.binaryType = 'arraybuffer';
    } catch {
      /* fakes */
    }
    this.dc.addEventListener('open', () => {
      if (!this.closed) opts.onOpen(this.dc);
    });

    pc.onnegotiationneeded = () => void this.negotiate();
    pc.onicecandidate = (ev) => {
      if (this.closed) return;
      const c = ev.candidate;
      if (!c || !c.candidate) {
        opts.signal({ kind: 'candidate_end' });
        return;
      }
      opts.signal({
        kind: 'candidate',
        candidate: c.candidate,
        sdpMid: c.sdpMid ?? null,
        sdpMLineIndex: c.sdpMLineIndex ?? null,
        usernameFragment: c.usernameFragment ?? null,
      });
    };
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === 'failed' || s === 'closed') this.reportFailed(`connection ${s}`);
    };
    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'failed') {
        this.reportFailed('ice failed');
        // A restart may still recover P2P later; the relay covers us meanwhile.
        try {
          pc.restartIce();
        } catch {
          /* old browsers */
        }
      }
    };
  }

  private reportFailed(reason: string): void {
    if (this.closed || this.failedReported) return;
    this.failedReported = true;
    this.opts.onFailed(reason);
  }

  private async negotiate(): Promise<void> {
    if (this.closed) return;
    try {
      this.makingOffer = true;
      await this.pc.setLocalDescription();
      const d = this.pc.localDescription;
      if (d && !this.closed) this.opts.signal({ kind: 'description', type: d.type, sdp: d.sdp });
    } catch (e) {
      this.env.log.warn('offer failed', e);
    } finally {
      this.makingOffer = false;
    }
  }

  /** Apply a signaling message from the other peer. */
  handle(data: SignalData): Promise<void> {
    this.chain = this.chain.then(() => this.apply(data)).catch((e) => this.env.log.warn('signal failed', e));
    return this.chain;
  }

  private async apply(data: SignalData): Promise<void> {
    if (this.closed) return;
    const pc = this.pc;
    if (data.kind === 'description') {
      if (data.type === 'rollback') return;
      // Perfect negotiation: an offer collides if we are making one or are not stable (answer pending excluded).
      const readyForOffer = !this.makingOffer && (pc.signalingState === 'stable' || this.settingRemoteAnswer);
      const collision = data.type === 'offer' && !readyForOffer;
      this.ignoreOffer = !this.opts.polite && collision;
      if (this.ignoreOffer) return;
      this.settingRemoteAnswer = data.type === 'answer';
      try {
        await pc.setRemoteDescription({
          type: data.type,
          ...(data.sdp !== undefined ? { sdp: data.sdp } : {}),
        });
      } finally {
        this.settingRemoteAnswer = false;
      }
      await this.drainCandidates();
      if (data.type === 'offer') {
        await pc.setLocalDescription();
        const d = pc.localDescription;
        if (d && !this.closed) this.opts.signal({ kind: 'description', type: d.type, sdp: d.sdp });
      }
      return;
    }
    if (data.kind === 'candidate') {
      const init: RTCIceCandidateInit = {
        candidate: data.candidate,
        ...(data.sdpMid !== undefined ? { sdpMid: data.sdpMid } : {}),
        ...(data.sdpMLineIndex !== undefined ? { sdpMLineIndex: data.sdpMLineIndex } : {}),
        ...(data.usernameFragment !== undefined ? { usernameFragment: data.usernameFragment } : {}),
      };
      if (!pc.remoteDescription) {
        if (this.pendingCandidates.length < 64) this.pendingCandidates.push(init);
        return;
      }
      await this.addCandidate(init);
      return;
    }
    // candidate_end: nothing required (browsers handle end-of-candidates implicitly).
  }

  private async drainCandidates(): Promise<void> {
    while (this.pendingCandidates.length > 0 && !this.closed)
      await this.addCandidate(this.pendingCandidates.shift()!);
  }

  private async addCandidate(c: RTCIceCandidateInit): Promise<void> {
    try {
      await this.pc.addIceCandidate(c);
    } catch (e) {
      if (!this.ignoreOffer) this.env.log.warn('addIceCandidate failed', e);
    }
  }

  get open(): boolean {
    return !this.closed && this.dc.readyState === 'open';
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    const pc = this.pc;
    pc.onnegotiationneeded = null;
    pc.onicecandidate = null;
    pc.onconnectionstatechange = null;
    pc.oniceconnectionstatechange = null;
    try {
      this.dc.close();
    } catch {
      /* closed */
    }
    try {
      pc.close();
    } catch {
      /* closed */
    }
  }
}
